import { useTranslation } from "react-i18next";
import { FileText } from "lucide-react";
import type { WhatsAppTemplateDto } from "@multi-chat/shared";
import { cn } from "../../lib/utils.js";
import {
  compactTemplatePreviewText,
  filterTemplatesForPicker,
} from "../inbox/template-picker.js";
import { extractBodyPreview } from "../inbox/template-send.js";
import { templateContentDir } from "../whatsapp/WhatsAppTemplatePreview/template-content-dir.js";
import { CampaignComboSelect } from "./CampaignComboSelect.js";
import { CampaignTemplateMetaChips } from "./CampaignTemplateCard.js";

export function CampaignTemplateSelect({
  templates,
  selectedTemplateId,
  disabled,
  onSelect,
}: {
  templates: readonly WhatsAppTemplateDto[];
  selectedTemplateId: number | null;
  disabled?: boolean;
  onSelect: (template: WhatsAppTemplateDto) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const selected = templates.find((item) => item.id === selectedTemplateId) ?? null;

  return (
    <CampaignComboSelect
      items={templates}
      getKey={(template) => String(template.id)}
      isSelected={(template) => template.id === selectedTemplateId}
      onSelect={onSelect}
      filter={(items, query) =>
        filterTemplatesForPicker(items, { query, language: null, category: null })
      }
      searchPlaceholder={t("campaigns.message.searchTemplates")}
      emptyLabel={t("campaigns.message.noTemplates")}
      noMatchesLabel={t("campaigns.message.noTemplateMatches")}
      disabled={disabled}
      ariaLabel={t("campaigns.message.chooseTemplate")}
      testId="campaign-template-select"
      menuTestId="campaign-template-select-menu"
      searchTestId="campaign-template-select-search"
      optionTestId={(template) => `campaign-template-select-option-${template.id}`}
      menuClassName="min-w-[min(22rem,calc(100vw-1rem))]"
      renderTrigger={() => (
        <>
          <span
            className={cn(
              "inline-grid size-9 shrink-0 place-items-center rounded-lg",
              selected ? "bg-accent-soft text-accent" : "bg-surface-muted text-subtle",
            )}
            aria-hidden="true"
          >
            <FileText size={16} />
          </span>
          {selected ? (
            <span className="grid min-w-0 flex-1 gap-1">
              <span className="truncate text-[0.875rem] font-semibold text-ink">
                {selected.name}
              </span>
              <CampaignTemplateMetaChips
                language={selected.language}
                category={selected.category}
                status={null}
              />
            </span>
          ) : (
            <span className="min-w-0 flex-1 truncate text-[0.8125rem] text-subtle">
              {t("campaigns.message.chooseTemplate")}
            </span>
          )}
        </>
      )}
      renderOption={(template, { selected: isSelected }) => {
        const excerpt = compactTemplatePreviewText(extractBodyPreview(template.components), 140);
        return (
          <span className="grid min-w-0 gap-1.5 py-0.5">
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span
                className={cn(
                  "min-w-0 truncate text-[0.8125rem]",
                  isSelected ? "font-semibold text-ink" : "font-medium text-ink",
                )}
              >
                {template.name}
              </span>
              <CampaignTemplateMetaChips
                language={template.language}
                category={template.category}
                status={null}
              />
            </span>
            {excerpt ? (
              <span
                className="line-clamp-2 rounded-md bg-surface-muted/70 px-2 py-1.5 text-[0.75rem] leading-snug text-muted"
                dir={templateContentDir(template.language)}
              >
                {excerpt}
              </span>
            ) : null}
          </span>
        );
      }}
    />
  );
}
